import { ChangeEvent } from 'react';
import { ITheme, useTheme } from 'hooks/useTheme';
import { getTranslationKey } from 'helpers/texting';
import { IoIosArrowDown, IoMdInformationCircleOutline } from 'react-icons/io';
import { UncontrolledTooltip } from 'reactstrap/lib';

import styled from 'styled-components';
import media from 'assets/css/media';

interface ISelectionItem {
  withDefaultPlaceholder?: boolean;
  value?: string;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  options: Array<{ name: string; value: string }>;
  label?: string;
  tooltip?: string;
}

const SelectItemWrapper = styled.div<{ theme: ITheme }>`
  position: relative;
  display: inline-block;
  padding: 15px;
  width: 100%;

  span {
    font-size: 20px;
  }

  select::-ms-expand {
    display: none;
  }

  select,
  .dropdown-container {
    font-size: 20px;
    width: 100%;
    appearance: none;
    border: none;
    background: none;
    border-bottom: solid 1px grey;
    border-radius: 0;

    @media all and (max-width: ${media.desktopXl}) {
      font-size: 16px;
    }
  }

  select {
    border-bottom: solid 1px ${(props) => props.theme.colors.modalFontColorLight} !important;

    &:focus-visible {
      outline: none;
    }

    color: ${(props) => props.theme.colors.modalFontColor} !important;

    option {
      color: ${(props) => props.theme.colors.modalFontColorLight} !important;
      background-color: ${(props) => props.theme.colors.modalBg} !important;
    }
  }

  color: ${(props) => props.theme.colors.modalFontColorLight};

  .select-wrapper {
    position: relative;
    z-index: 2;
  }
`;

const Label = styled.h6`
  font-weight: bold;
  display: inline;
`;

const StyledArrow = styled(IoIosArrowDown)<{ theme: ITheme }>`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  z-index: 2;
  pointer-events: none;
`;

const TitleWrap = styled.span`
  display: flex;
`;

const InfoIconWrap = styled.span`
  margin-left: 5px;
`;

const StyledUncontrolledTooltip = styled(UncontrolledTooltip)`
  z-index: 999;
  transition: none;
`;

const SelectContainer = styled.div`
  position: relative;
  z-index: 2;
`;

export const SingleSelectItem = ({
  withDefaultPlaceholder,
  value = '',
  onChange,
  options,
  label = '',
  tooltip = '',
  ...props
}: ISelectionItem) => {
  const [theme] = useTheme();

  return (
    <SelectItemWrapper theme={theme} {...props}>
      <TitleWrap>
        {label && <Label>{label}</Label>}

        {!!tooltip && (
          <>
            <InfoIconWrap id={label}>
              <IoMdInformationCircleOutline />
            </InfoIconWrap>

            <StyledUncontrolledTooltip target={label}>{tooltip}</StyledUncontrolledTooltip>
          </>
        )}
      </TitleWrap>

      <SelectContainer>
        <div className="select-wrapper">
          <select className="cursor-pointer" onChange={onChange} value={value}>
            {withDefaultPlaceholder && (
              <option disabled value="default">
                {getTranslationKey('pleaseSelect')}
              </option>
            )}
            {options.map((item: any) => (
              <option key={item.value} value={item.value}>
                {item.name}
              </option>
            ))}
          </select>
        </div>

        <StyledArrow />
      </SelectContainer>
    </SelectItemWrapper>
  );
};
